import type { LocaleCode, PaymentCreateView, PaymentStatusView } from "@fiket/booth-schema";
import { unwrapEnvelope } from "../../lib/apiEnvelope";

/**
 * 결제 공개 API (BOOTH-61 §5.3) — 키오스크 몫은 생성·폴링·취소 셋. 응답에 Toss 키는 없고 QR 은 payUrl 만 쓴다.
 * 금액은 서버가 계산한다(본문에 금액 없음, F64). 폴링에 `?touch=0` 을 붙이지 않는다 — lastPolledAt 갱신이
 * "키오스크가 아직 보고 있다" 판정(reconcile 가 늦게 들어온 승인을 유지할지 전액 취소할지)의 근거다 (Ruling 17).
 */
const JSON_HEADERS = { "Content-Type": "application/json" };

/** 매장 Wi-Fi 가 불안정할 때 취소·생성이 수십 초 걸리면 손님이 00:00 화면에 갇힌다 — 10초 상한, 취소 실패는 서버 expiresAt 만료에 맡긴다(F30). */
const REQUEST_TIMEOUT_MS = 10_000;

function withTimeout(callerSignal?: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), REQUEST_TIMEOUT_MS);
  const abort = () => { clearTimeout(timer); controller.abort(); };
  if (callerSignal) {
    if (callerSignal.aborted) { clearTimeout(timer); controller.abort(); }
    else callerSignal.addEventListener("abort", abort, { once: true });
  }
  return controller.signal;
}

export async function createPayment(
  body: { boothId: string; withVideo: boolean; language: LocaleCode; visitId?: string },
  signal?: AbortSignal
): Promise<PaymentCreateView> {
  const res = await fetch("/api/payments", { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body), signal: withTimeout(signal) });
  return unwrapEnvelope<PaymentCreateView>(res);
}

export async function fetchPayment(paymentId: string, signal?: AbortSignal): Promise<PaymentStatusView> {
  const res = await fetch(`/api/payments/${encodeURIComponent(paymentId)}`, { cache: "no-store", signal: withTimeout(signal) });
  return unwrapEnvelope<PaymentStatusView>(res);
}

/** pending·failed → cancelled. paid 면 409 ALREADY_PAID(ApiEnvelopeError), 이미 종결이면 멱등 200 */
export async function cancelPayment(paymentId: string, signal?: AbortSignal): Promise<PaymentStatusView> {
  const res = await fetch(`/api/payments/${encodeURIComponent(paymentId)}/cancel`, {
    method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ reason: "손님 취소" }), signal: withTimeout(signal),
  });
  return unwrapEnvelope<PaymentStatusView>(res);
}
